import { useState } from 'react';
import axios from 'axios';
import { Mail, MessageCircle, CheckCircle2, Phone, Linkedin, Instagram, Send } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '@/context/ThemeContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const { isDark } = useTheme();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [submitStatus, setSubmitStatus] = useState({ type: '', message: '' });

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitStatus({
        type: 'success',
        message: 'Thank you for reaching out! We\'ll get back to you soon.'
      });
      setFormData({ name: '', email: '', subject: '', message: '' });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      setSubmitStatus({
        type: 'error',
        message: 'Failed to send message. Please try again or contact us directly at contact@prodizzy.com'
      });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const contactOptions = [
    {
      icon: Mail,
      title: 'Email Us',
      description: 'For general inquiries and support',
      link: 'mailto:contact@prodizzy.com',
      linkText: 'contact@prodizzy.com',
      color: 'from-blue-400 to-blue-500'
    },
    {
      icon: MessageCircle,
      title: 'Join WhatsApp',
      description: 'Direct access to our community',
      link: 'https://chat.whatsapp.com/HkxDeCqP5pxHx2zJ0hm5I5?mode=wwt',
      linkText: 'WhatsApp Community',
      color: 'from-green-400 to-green-500'
    },
    {
      icon: Linkedin,
      title: 'LinkedIn',
      description: 'Follow our page',
      link: 'https://www.linkedin.com/company/prodizzy/',
      linkText: 'LinkedIn Profile',
      color: 'from-blue-500 to-blue-600'
    },
    {
      icon: Instagram,
      title: 'Instagram',
      description: 'See our latest updates and stories',
      link: '#',
      linkText: '@prodizzy',
      color: 'from-pink-400 to-pink-500'
    }
  ];

  return (
    <motion.div 
      className={`pt-20 min-h-screen transition-colors duration-300 ${
        isDark ? 'bg-gradient-to-br from-[#0f0520] via-[#1a0b2e] to-[#2d1b4e]' : 'bg-gray-50'
      }`}
      data-testid="contact-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      {/* Hero Section */}
      <section className={`py-20 ${
        isDark 
          ? 'bg-gradient-to-br from-purple-900/30 via-transparent to-purple-900/30' 
          : 'bg-gradient-to-br from-indigo-600 to-purple-700'
      }`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          {/* Badge */}
          <motion.div
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-purple-500/20 border border-purple-500/30 mb-8"
          >
            <Phone className="w-4 h-4 text-purple-300" />
            <span className="text-sm font-medium text-purple-200">
              Get in Touch
            </span>
          </motion.div>

          {/* Heading */}
          <motion.h1 
            className="text-4xl md:text-5xl font-bold mb-6 text-white"
            data-testid="contact-page-title"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            Contact Us
          </motion.h1>

          {/* Subheading */}
          <motion.p 
            className={`text-xl max-w-3xl mx-auto ${isDark ? 'text-gray-300' : 'text-white/90'}`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            Have a question, partnership proposal, or feedback? We would love to hear from you.
          </motion.p>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Get in Touch Options */}
            <div>
              <h2 className={`text-3xl font-bold mb-8 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Get in Touch
              </h2>
              <p className={`mb-8 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                Choose your preferred way to connect with us
              </p>
              <div className="space-y-6">
                {contactOptions.map((option, index) => {
                  const Icon = option.icon;

                  return (
                    <motion.a
                      key={index}
                      href={option.link}
                      target={option.link.startsWith('http') ? '_blank' : undefined}
                      rel={option.link.startsWith('http') ? 'noopener noreferrer' : undefined}
                      className={`block rounded-2xl shadow-lg p-6 border-2 transition-all ${
                        isDark
                          ? 'bg-white/5 border-purple-500/30 hover:border-purple-400'
                          : 'bg-white border-indigo-100 hover:border-indigo-400'
                      }`}
                      data-testid={`contact-option-${index}`}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                      whileHover={{ y: -5, scale: 1.02 }}
                    >
                      <div className="flex items-start">
                        <div className={`p-3 rounded-xl mr-4 bg-gradient-to-br ${option.color}`}>
                          <Icon size={24} className="text-white" />
                        </div>
                        <div className="flex-1">
                          <h3 className={`text-xl font-bold mb-1 ${
                            isDark ? 'text-white' : 'text-gray-900'
                          }`}>{option.title}</h3>
                          <p className={`text-sm mb-2 ${
                            isDark ? 'text-gray-400' : 'text-gray-600'
                          }`}>{option.description}</p>
                          <span className="text-purple-400 hover:text-purple-300 font-semibold">
                            {option.linkText} →
                          </span>
                        </div>
                      </div>
                    </motion.a>
                  );
                })}
              </div>
            </div>

            {/* Direct Message Form */}
            <div>
              <h2 className={`text-3xl font-bold mb-8 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Send Us a Message
              </h2>
              <p className={`mb-8 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                We typically respond within 24 hours
              </p>

              <AnimatePresence>
                {submitStatus.message && (
                  <motion.div
                    className={`mb-6 p-4 rounded-2xl flex items-start shadow-lg ${
                      submitStatus.type === 'success'
                        ? 'bg-green-50 border-2 border-green-200 text-green-800'
                        : 'bg-red-50 border-2 border-red-200 text-red-800'
                    }`}
                    data-testid="contact-submit-status"
                    initial={{ opacity: 0, scale: 0.9, y: -20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ duration: 0.5 }}
                  >
                    {submitStatus.type === 'success' && <CheckCircle2 className="mr-2 flex-shrink-0 mt-0.5" size={20} />}
                    <span>{submitStatus.message}</span>
                  </motion.div>
                )}
              </AnimatePresence>

              <motion.div 
                className={`rounded-2xl shadow-lg p-8 border-2 ${
                  isDark
                    ? 'bg-white/5 border-purple-500/30'
                    : 'bg-white border-indigo-200'
                }`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label className={`block text-sm font-semibold mb-2 ${
                      isDark ? 'text-gray-300' : 'text-gray-700'
                    }`} htmlFor="name">
                      Name *
                    </label>
                    <motion.input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      placeholder="Your name"
                      className={`w-full px-4 py-3 border-2 rounded-xl transition shadow-sm hover:shadow-md ${
                        isDark
                          ? 'bg-[#1a0b2e] border-purple-500/30 text-white placeholder-gray-500 focus:ring-2 focus:ring-purple-500 focus:border-purple-500'
                          : 'bg-white border-gray-300 text-gray-900 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500'
                      }`}
                      data-testid="contact-name-input"
                      whileFocus={{ scale: 1.02 }}
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-semibold mb-2 ${
                      isDark ? 'text-gray-300' : 'text-gray-700'
                    }`} htmlFor="email">
                      Email *
                    </label>
                    <motion.input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      placeholder="your.email@example.com"
                      className={`w-full px-4 py-3 border-2 rounded-xl transition shadow-sm hover:shadow-md ${
                        isDark
                          ? 'bg-[#1a0b2e] border-purple-500/30 text-white placeholder-gray-500 focus:ring-2 focus:ring-purple-500 focus:border-purple-500'
                          : 'bg-white border-gray-300 text-gray-900 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500'
                      }`}
                      data-testid="contact-email-input"
                      whileFocus={{ scale: 1.02 }}
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-semibold mb-2 ${
                      isDark ? 'text-gray-300' : 'text-gray-700'
                    }`} htmlFor="subject">
                      Subject *
                    </label>
                    <motion.input
                      type="text"
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      required
                      placeholder="What is this about?"
                      className={`w-full px-4 py-3 border-2 rounded-xl transition shadow-sm hover:shadow-md ${
                        isDark
                          ? 'bg-[#1a0b2e] border-purple-500/30 text-white placeholder-gray-500 focus:ring-2 focus:ring-purple-500 focus:border-purple-500'
                          : 'bg-white border-gray-300 text-gray-900 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500'
                      }`}
                      data-testid="contact-subject-input"
                      whileFocus={{ scale: 1.02 }}
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-semibold mb-2 ${
                      isDark ? 'text-gray-300' : 'text-gray-700'
                    }`} htmlFor="message">
                      Message *
                    </label>
                    <motion.textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      rows={6}
                      required
                      placeholder="Tell us more..."
                      className={`w-full px-4 py-3 border-2 rounded-xl transition shadow-sm hover:shadow-md resize-none ${
                        isDark
                          ? 'bg-[#1a0b2e] border-purple-500/30 text-white placeholder-gray-500 focus:ring-2 focus:ring-purple-500 focus:border-purple-500'
                          : 'bg-white border-gray-300 text-gray-900 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500'
                      }`}
                      data-testid="contact-message-input"
                      whileFocus={{ scale: 1.01 }}
                    />
                  </div>

                  <motion.button
                    type="submit"
                    className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white px-8 py-3 rounded-full font-semibold hover:from-purple-700 hover:to-pink-700 transition shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
                    data-testid="contact-submit-button"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Send size={20} />
                    Send Message
                  </motion.button>
                </form>
              </motion.div>

              {/* Office Hours */}
              <motion.div
                className={`rounded-2xl shadow-lg p-6 border-2 mt-8 ${
                  isDark
                    ? 'bg-white/5 border-purple-500/30'
                    : 'bg-white border-indigo-200'
                }`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <h3 className={`text-xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Office Hours
                </h3>
                <div className={`space-y-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  <div className="flex justify-between">
                    <span>Monday - Friday</span>
                    <span className="font-semibold">9:00 AM - 6:00 PM</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Saturday</span>
                    <span className="font-semibold">10:00 AM - 4:00 PM</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Sunday</span>
                    <span className="font-semibold text-gray-500">Closed</span>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Partnerships Section */}
      <section className={`py-16 ${
        isDark 
          ? 'bg-gradient-to-r from-purple-900/50 to-purple-800/50' 
          : 'bg-gradient-to-r from-indigo-600 to-purple-600'
      }`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            {/* Badge */}
            <motion.div
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-purple-500/20 border border-purple-500/30 mb-6"
            >
              <span className="text-sm font-medium text-purple-200">
                Partnerships
              </span>
            </motion.div>

            <h2 className="text-3xl font-bold mb-4 text-white">
              Looking to Partner?
            </h2>
            <p className={`text-lg mb-8 max-w-2xl mx-auto ${
              isDark ? 'text-gray-300' : 'text-white/90'
            }`}>
              We're always open to collaborations with educational institutions, organizations, and 
              companies that share our vision of empowering young builders.
            </p>
            <motion.a
              href="mailto:partnerships@prodizzy.com"
              className={`inline-flex items-center gap-2 px-8 py-3 rounded-full font-semibold transition shadow-lg hover:shadow-xl ${
                isDark
                  ? 'bg-purple-500 text-white hover:bg-purple-600'
                  : 'bg-white text-indigo-600 hover:bg-gray-100'
              }`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Mail size={20} />
              partnerships@prodizzy.com
            </motion.a>
          </motion.div>
        </div>
      </section>
    </motion.div>
  );
};

export default Contact;
